import { fetchByIds, fetchOrgMembersWith } from "@/lib/large-org";
/**
 * Contractor Partners — server functions.
 *
 * Every handler runs as the signed-in user through `requireSupabaseAuth`, so the
 * RLS policies and the `can_manage_contractor_*` / `is_contractor_*` database
 * functions are the authorization boundary. Nothing here hides data as a
 * substitute for a policy: a caller outside their scope gets an empty result or
 * a database error, whichever the policy dictates.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const MANAGER_ROLES = ["org_owner", "admin", "supervisor", "site_supervisor"];

/** Sites the acting user is restricted to (site assignments + region expansion). */
async function resolveScope(
  supabase: any,
  orgId: string,
  userId: string,
): Promise<{ role: string | null; orgWide: boolean; scopedSiteIds: string[]; canManage: boolean }> {
  const { data: membership } = await supabase
    .from("organization_members")
    .select("role")
    .eq("organization_id", orgId)
    .eq("user_id", userId)
    .maybeSingle();
  const role: string | null = membership?.role ?? null;
  if (!role || !MANAGER_ROLES.includes(role)) {
    return { role, orgWide: false, scopedSiteIds: [], canManage: false };
  }
  if (role === "org_owner" || role === "admin") {
    return { role, orgWide: true, scopedSiteIds: [], canManage: true };
  }

  const nowIso = new Date().toISOString();
  const { data: assignments } = await supabase
    .from("user_site_assignments")
    .select("site_id, region_id, starts_at, ends_at")
    .eq("organization_id", orgId)
    .eq("user_id", userId);
  const active = (assignments ?? []).filter(
    (a: any) =>
      (!a.starts_at || a.starts_at <= nowIso) && (!a.ends_at || a.ends_at >= nowIso),
  );
  if (active.length === 0) {
    // A general supervisor with no site/region restriction stays org-wide.
    return { role, orgWide: role === "supervisor", scopedSiteIds: [], canManage: true };
  }
  const siteIds = new Set<string>(
    active.filter((a: any) => a.site_id).map((a: any) => a.site_id as string),
  );
  const regionIds = active.filter((a: any) => a.region_id).map((a: any) => a.region_id as string);
  if (regionIds.length) {
    const { data: rs } = await supabase
      .from("site_regions")
      .select("site_id")
      .in("region_id", regionIds);
    for (const r of rs ?? []) siteIds.add(r.site_id as string);
  }
  return { role, orgWide: false, scopedSiteIds: [...siteIds], canManage: true };
}

export const getContractorScope = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ orgId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    const scope = await resolveScope(context.supabase, data.orgId, context.userId);
    return scope;
  });

// ---------------------------------------------------------------------------
// Inviting organization side
// ---------------------------------------------------------------------------

export const listPartnerships = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ orgId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: rows, error } = await supabase
      .from("contractor_partnerships")
      .select("*")
      .eq("inviting_org_id", data.orgId)
      .order("created_at", { ascending: false });
    if (error) throw new Response(error.message, { status: 400 });
    const ids = (rows ?? []).map((r: any) => r.id);
    if (ids.length === 0) return { partnerships: [] as any[] };

    const [{ data: sites }, { data: members }, { data: shifts }, { data: periods }] =
      await Promise.all([
        supabase
          .from("contractor_site_assignments")
          .select("partnership_id, site_id, active, sites(name)")
          .in("partnership_id", ids),
        supabase
          .from("contractor_members")
          .select("partnership_id, user_id, contractor_role, active")
          .in("partnership_id", ids),
        supabase
          .from("shifts")
          .select("id, contractor_partnership_id, status, officer_id, starts_at")
          .in("contractor_partnership_id", ids),
        supabase
          .from("contractor_pay_periods")
          .select("id, partnership_id, status, period_start, period_end, total_hours")
          .in("partnership_id", ids)
          .order("period_start", { ascending: false }),
      ]);

    const partnerships = (rows ?? []).map((p: any) => {
      const pSites = (sites ?? []).filter((s: any) => s.partnership_id === p.id && s.active);
      const pMembers = (members ?? []).filter((m: any) => m.partnership_id === p.id && m.active);
      const pShifts = (shifts ?? []).filter((s: any) => s.contractor_partnership_id === p.id);
      const pPeriods = (periods ?? []).filter((x: any) => x.partnership_id === p.id);
      return {
        ...p,
        sites: pSites.map((s: any) => ({ id: s.site_id, name: s.sites?.name ?? "Site" })),
        memberCount: pMembers.length,
        unclaimedCount: pShifts.filter((s: any) => !s.officer_id && s.status !== "cancelled").length,
        claimedCount: pShifts.filter((s: any) => s.officer_id && s.status !== "cancelled").length,
        latestPayPeriod: pPeriods[0] ?? null,
      };
    });
    return { partnerships };
  });

export const getPartnership = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ partnershipId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: partnership, error } = await supabase
      .from("contractor_partnerships")
      .select("*")
      .eq("id", data.partnershipId)
      .maybeSingle();
    if (error) throw new Response(error.message, { status: 400 });
    if (!partnership) throw new Response("Not found", { status: 404 });

    const [{ data: sites }, { data: members }, { data: invites }, { data: shifts }, { data: periods }] =
      await Promise.all([
        supabase
          .from("contractor_site_assignments")
          .select("id, site_id, active, created_at, sites(name, address, city)")
          .eq("partnership_id", data.partnershipId),
        supabase
          .from("contractor_members")
          .select("id, user_id, contractor_role, active, created_at")
          .eq("partnership_id", data.partnershipId),
        supabase
          .from("contractor_partner_invites")
          .select("id, email, kind, token, accepted_at, expires_at, created_at")
          .eq("partnership_id", data.partnershipId)
          .order("created_at", { ascending: false }),
        supabase
          .from("shifts")
          .select("id, site_id, starts_at, ends_at, status, officer_id, role_label, notes")
          .eq("contractor_partnership_id", data.partnershipId)
          .order("starts_at", { ascending: true }),
        supabase
          .from("contractor_pay_periods")
          .select("*")
          .eq("partnership_id", data.partnershipId)
          .order("period_start", { ascending: false }),
      ]);

    const userIds = [...new Set((members ?? []).map((m: any) => m.user_id))];
    let profiles: any[] = [];
    if (userIds.length) {
      const { data: p } = await fetchByIds<any>("profiles", "id, full_name, email", userIds, "id", supabase).then((d) => ({ data: d, error: null }));
      profiles = p ?? [];
    }

    return {
      partnership,
      sites: sites ?? [],
      members: (members ?? []).map((m: any) => ({
        ...m,
        profile: profiles.find((p) => p.id === m.user_id) ?? null,
      })),
      invites: invites ?? [],
      shifts: shifts ?? [],
      payPeriods: periods ?? [],
    };
  });

export const inviteContractorPartner = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      orgId: uuid,
      contractorName: z.string().min(2).max(160),
      email: z.string().email().max(254),
      purpose: z.string().max(500).optional(),
      relationshipType: z.enum(["temporary", "ongoing"]).default("temporary"),
      startsOn: isoDate.optional(),
      endsOn: isoDate.optional(),
      siteIds: z.array(uuid).max(200).default([]),
      kind: z.enum(["organization", "individual"]).default("organization"),
      origin: z.string().url().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const scope = await resolveScope(supabase, data.orgId, userId);
    if (!scope.canManage) throw new Response("You do not have contractor partner access", { status: 403 });
    const siteIds = scope.orgWide
      ? data.siteIds
      : data.siteIds.filter((id) => scope.scopedSiteIds.includes(id));
    if (siteIds.length !== data.siteIds.length) {
      throw new Response("One or more sites are outside your scope", { status: 403 });
    }

    const { data: org } = await supabase
      .from("organizations")
      .select("name")
      .eq("id", data.orgId)
      .maybeSingle();

    const { data: partnership, error } = await supabase
      .from("contractor_partnerships")
      .insert({
        inviting_org_id: data.orgId,
        inviting_org_name: org?.name ?? null,
        contractor_name: data.contractorName.trim(),
        purpose: data.purpose ?? null,
        relationship_type: data.relationshipType,
        starts_on: data.startsOn ?? null,
        ends_on: data.endsOn ?? null,
        created_by: userId,
      } as any)
      .select("*")
      .single();
    if (error || !partnership) throw new Response(error?.message ?? "Could not create", { status: 400 });

    const { data: invite, error: invErr } = await supabase
      .from("contractor_partner_invites")
      .insert({
        partnership_id: partnership.id,
        inviting_org_id: data.orgId,
        email: data.email.trim().toLowerCase(),
        kind: data.kind,
        proposed_site_ids: siteIds,
        invited_by: userId,
      } as any)
      .select("id, token, email, expires_at")
      .single();
    if (invErr || !invite) throw new Response(invErr?.message ?? "Could not invite", { status: 400 });

    await supabase.from("audit_log").insert({
      organization_id: data.orgId,
      actor_id: userId,
      action: "contractor.invited",
      target_type: "contractor_partnership",
      target_id: partnership.id,
      metadata: { email: invite.email, kind: data.kind, site_count: siteIds.length },
    } as any);

    const { sendContractorInviteEmail, safeOrigin } = await import("@/lib/contractor-email.server");
    let siteNames: string[] = [];
    if (siteIds.length) {
      const { data: s } = await supabase.from("sites").select("name").in("id", siteIds);
      siteNames = (s ?? []).map((x: any) => x.name);
    }
    const link = `${safeOrigin(data.origin)}/accept-contract/${invite.token}`;
    const mail = await sendContractorInviteEmail({
      to: invite.email,
      invitingOrgName: org?.name ?? "MissionReady",
      contractorName: data.contractorName.trim(),
      purpose: data.purpose,
      relationshipType: data.relationshipType,
      startsOn: data.startsOn,
      endsOn: data.endsOn,
      siteNames,
      expiresAt: invite.expires_at,
      link,
      kind: data.kind,
    });

    return {
      partnershipId: partnership.id,
      token: invite.token,
      emailSent: mail.sent,
      emailError: mail.error,
    };
  });

export const resendContractorInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ inviteId: uuid, origin: z.string().url().optional() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: invite, error } = await supabase
      .from("contractor_partner_invites")
      .update({ expires_at: new Date(Date.now() + 14 * 86400000).toISOString() } as any)
      .eq("id", data.inviteId)
      .select("token, email, kind, expires_at, partnership_id")
      .single();
    if (error || !invite) throw new Response(error?.message ?? "Not found", { status: 400 });
    const { data: p } = await supabase
      .from("contractor_partnerships")
      .select("contractor_name, inviting_org_name, purpose, relationship_type, starts_on, ends_on")
      .eq("id", invite.partnership_id)
      .maybeSingle();
    const { sendContractorInviteEmail, safeOrigin } = await import("@/lib/contractor-email.server");
    const mail = await sendContractorInviteEmail({
      to: invite.email,
      invitingOrgName: p?.inviting_org_name ?? "MissionReady",
      contractorName: p?.contractor_name ?? "Contractor",
      purpose: p?.purpose,
      relationshipType: p?.relationship_type ?? "temporary",
      startsOn: p?.starts_on,
      endsOn: p?.ends_on,
      expiresAt: invite.expires_at,
      link: `${safeOrigin(data.origin)}/accept-contract/${invite.token}`,
      kind: invite.kind as any,
      resent: true,
    });
    return { token: invite.token, emailSent: mail.sent, emailError: mail.error };
  });

export const revokeContractorInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ inviteId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    // Historical records are preserved: an unused invitation simply expires now.
    const { error } = await context.supabase
      .from("contractor_partner_invites")
      .update({ expires_at: new Date().toISOString() } as any)
      .eq("id", data.inviteId);
    if (error) throw new Response(error.message, { status: 400 });
    return { ok: true };
  });

export const updatePartnership = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      partnershipId: uuid,
      status: z.enum(["invited", "accepted", "active", "inactive", "completed", "revoked"]).optional(),
      relationshipType: z.enum(["temporary", "ongoing"]).optional(),
      startsOn: isoDate.nullable().optional(),
      endsOn: isoDate.nullable().optional(),
      clientInfoAccess: z.boolean().optional(),
      purpose: z.string().max(500).nullable().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: Record<string, unknown> = {};
    if (data.status) patch["status"] = data.status;
    if (data.relationshipType) patch["relationship_type"] = data.relationshipType;
    if (data.startsOn !== undefined) patch["starts_on"] = data.startsOn;
    if (data.endsOn !== undefined) patch["ends_on"] = data.endsOn;
    if (data.clientInfoAccess !== undefined) patch["client_info_access"] = data.clientInfoAccess;
    if (data.purpose !== undefined) patch["purpose"] = data.purpose;

    const { data: row, error } = await supabase
      .from("contractor_partnerships")
      .update(patch as any)
      .eq("id", data.partnershipId)
      .select("*")
      .single();
    if (error || !row) throw new Response(error?.message ?? "Could not update", { status: 400 });

    await supabase.from("audit_log").insert({
      organization_id: row.inviting_org_id,
      actor_id: userId,
      action: "contractor.updated",
      target_type: "contractor_partnership",
      target_id: row.id,
      metadata: patch,
    } as any);
    return { partnership: row };
  });

export const assignContractorSites = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ partnershipId: uuid, siteIds: z.array(uuid).min(1).max(200) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: p, error: pErr } = await supabase
      .from("contractor_partnerships")
      .select("id, inviting_org_id, contractor_name")
      .eq("id", data.partnershipId)
      .maybeSingle();
    if (pErr || !p) throw new Response("Not found", { status: 404 });
    const scope = await resolveScope(supabase, p.inviting_org_id, userId);
    if (!scope.canManage) throw new Response("Not authorized", { status: 403 });
    const outside = scope.orgWide
      ? []
      : data.siteIds.filter((id) => !scope.scopedSiteIds.includes(id));
    if (outside.length) throw new Response("Site outside your scope", { status: 403 });

    const { error } = await supabase
      .from("contractor_site_assignments")
      .upsert(
        data.siteIds.map((site_id) => ({
          partnership_id: data.partnershipId,
          inviting_org_id: p.inviting_org_id,
          site_id,
          assigned_by: userId,
          active: true,
        })) as any,
        { onConflict: "partnership_id,site_id" },
      );
    if (error) throw new Response(error.message, { status: 400 });

    await supabase.from("audit_log").insert({
      organization_id: p.inviting_org_id,
      actor_id: userId,
      action: "contractor.sites_assigned",
      target_type: "contractor_partnership",
      target_id: p.id,
      metadata: { site_ids: data.siteIds },
    } as any);

    const { data: members } = await supabase
      .from("contractor_members")
      .select("user_id")
      .eq("partnership_id", data.partnershipId)
      .eq("active", true);
    const recipients = (members ?? []).map((m: any) => m.user_id);
    if (recipients.length) {
      await supabase.rpc("fanout_notification", {
        _org: p.inviting_org_id,
        _recipients: recipients,
        _actor: userId,
        _source: "contractor",
        _source_id: p.id,
        _event_type: "contractor_site_assigned",
        _severity: "info",
        _title: "New site assignment",
        _body: "You have been assigned to a new site. Contracted shifts may be available.",
        _link: "/contractor",
        _metadata: {},
      } as any);
    }
    return { ok: true };
  });

export const setContractorSiteActive = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ assignmentId: uuid, active: z.boolean() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("contractor_site_assignments")
      .update({ active: data.active } as any)
      .eq("id", data.assignmentId);
    if (error) throw new Response(error.message, { status: 400 });
    return { ok: true };
  });

export const publishContractorShift = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      partnershipId: uuid,
      siteId: uuid,
      startsAt: z.string().min(10),
      endsAt: z.string().min(10),
      roleLabel: z.string().max(120).optional(),
      notes: z.string().max(1000).optional(),
      positions: z.number().int().min(1).max(20).default(1),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: p } = await supabase
      .from("contractor_partnerships")
      .select("id, inviting_org_id")
      .eq("id", data.partnershipId)
      .maybeSingle();
    if (!p) throw new Response("Not found", { status: 404 });
    const scope = await resolveScope(supabase, p.inviting_org_id, userId);
    if (!scope.canManage) throw new Response("Not authorized", { status: 403 });
    if (!scope.orgWide && !scope.scopedSiteIds.includes(data.siteId)) {
      throw new Response("Site outside your scope", { status: 403 });
    }
    const { data: assigned } = await supabase
      .from("contractor_site_assignments")
      .select("id")
      .eq("partnership_id", data.partnershipId)
      .eq("site_id", data.siteId)
      .eq("active", true)
      .maybeSingle();
    if (!assigned) throw new Response("Assign the contractor to this site first", { status: 400 });

    const rows = Array.from({ length: data.positions }, () => ({
      organization_id: p.inviting_org_id,
      site_id: data.siteId,
      contractor_partnership_id: data.partnershipId,
      starts_at: data.startsAt,
      ends_at: data.endsAt,
      role_label: data.roleLabel ?? "Contracted post",
      notes: data.notes ?? null,
      status: "unclaimed",
      officer_id: null,
      created_by: userId,
    }));
    const { data: inserted, error } = await supabase.from("shifts").insert(rows as any).select("id");
    if (error) throw new Response(error.message, { status: 400 });

    const { data: members } = await supabase
      .from("contractor_members")
      .select("user_id")
      .eq("partnership_id", data.partnershipId)
      .eq("active", true);
    const recipients = (members ?? []).map((m: any) => m.user_id);
    if (recipients.length) {
      await supabase.rpc("fanout_notification", {
        _org: p.inviting_org_id,
        _recipients: recipients,
        _actor: userId,
        _source: "contractor",
        _source_id: p.id,
        _event_type: "contractor_shifts_published",
        _severity: "info",
        _title: "Shifts available to claim",
        _body: `${data.positions} contracted shift${data.positions > 1 ? "s" : ""} posted to the Shift Claim board.`,
        _link: "/contractor/shift-claim",
        _metadata: {},
      } as any);
    }
    return { created: inserted?.length ?? 0 };
  });

export const cancelContractorShift = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ shiftId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("shifts")
      .update({ status: "cancelled" } as any)
      .eq("id", data.shiftId);
    if (error) throw new Response(error.message, { status: 400 });
    return { ok: true };
  });

// ---------------------------------------------------------------------------
// Invitation acceptance
// ---------------------------------------------------------------------------

/** Public preview of a contractor invitation (no session required). */
export const previewContractorInvite = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ token: uuid }).parse(d))
  .handler(async ({ data }) => {
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    const client = createClient<Database>(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false, autoRefreshToken: false },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
    const { data: rows, error } = await client.rpc("get_contractor_invite", { _token: data.token } as any);
    if (error) return { invite: null, error: "Data source unavailable" };
    const invite = Array.isArray(rows) ? rows[0] : rows;
    return { invite: invite ?? null };
  });

export const acceptContractorInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ token: uuid, organizationName: z.string().min(2).max(160).optional() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: partnershipId, error } = await context.supabase.rpc("accept_contractor_invite", {
      _token: data.token,
      _org_name: data.organizationName ?? null,
    } as any);
    if (error) throw new Response(error.message, { status: 400 });
    return { partnershipId };
  });

/** A contractor owner invites their own personnel into the relationship. */
export const inviteContractorMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      partnershipId: uuid,
      email: z.string().email().max(254),
      origin: z.string().url().optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: p } = await supabase
      .from("contractor_partnerships")
      .select("id, inviting_org_id, inviting_org_name, contractor_name, relationship_type, starts_on, ends_on, purpose")
      .eq("id", data.partnershipId)
      .maybeSingle();
    if (!p) throw new Response("Not found", { status: 404 });

    const { data: invite, error } = await supabase
      .from("contractor_partner_invites")
      .insert({
        partnership_id: data.partnershipId,
        inviting_org_id: p.inviting_org_id,
        email: data.email.trim().toLowerCase(),
        kind: "member",
        invited_by: userId,
      } as any)
      .select("id, token, email, expires_at")
      .single();
    if (error || !invite) throw new Response(error?.message ?? "Could not invite", { status: 400 });

    const { sendContractorInviteEmail, safeOrigin } = await import("@/lib/contractor-email.server");
    const mail = await sendContractorInviteEmail({
      to: invite.email,
      invitingOrgName: p.inviting_org_name ?? "MissionReady",
      contractorName: p.contractor_name,
      purpose: p.purpose,
      relationshipType: p.relationship_type,
      startsOn: p.starts_on,
      endsOn: p.ends_on,
      expiresAt: invite.expires_at,
      link: `${safeOrigin(data.origin)}/accept-contract/${invite.token}`,
      kind: "member",
    });
    return { token: invite.token, emailSent: mail.sent, emailError: mail.error };
  });

export const setContractorMemberActive = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ memberId: uuid, active: z.boolean() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("contractor_members")
      .update({ active: data.active } as any)
      .eq("id", data.memberId);
    if (error) throw new Response(error.message, { status: 400 });
    return { ok: true };
  });

// ---------------------------------------------------------------------------
// Contractor side
// ---------------------------------------------------------------------------

/** Everything the signed-in contractor user is allowed to see. */
export const getContractorWorkspace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: memberships, error } = await supabase
      .from("contractor_members")
      .select("id, partnership_id, contractor_role, active, contractor_org_id")
      .eq("user_id", userId)
      .eq("active", true);
    if (error) throw new Response(error.message, { status: 400 });
    const ids = (memberships ?? []).map((m: any) => m.partnership_id);
    if (ids.length === 0) {
      return { memberships: [], partnerships: [], sites: [], postOrders: [], shifts: [], payPeriods: [], teammates: [] };
    }

    const [{ data: partnerships }, { data: siteRows }, { data: shifts }, { data: periods }, { data: teammates }] =
      await Promise.all([
        supabase.from("contractor_partnerships").select("*").in("id", ids),
        supabase
          .from("contractor_site_assignments")
          .select("partnership_id, site_id, active, sites(id, name, address, city, latitude, longitude)")
          .in("partnership_id", ids)
          .eq("active", true),
        supabase
          .from("shifts")
          .select("id, site_id, starts_at, ends_at, status, officer_id, role_label, notes, contractor_partnership_id")
          .in("contractor_partnership_id", ids)
          .order("starts_at", { ascending: true }),
        supabase
          .from("contractor_pay_periods")
          .select("*")
          .in("partnership_id", ids)
          .order("period_start", { ascending: false }),
        supabase
          .from("contractor_members")
          .select("id, partnership_id, user_id, contractor_role, active")
          .in("partnership_id", ids),
      ]);

    const siteIds = [...new Set((siteRows ?? []).map((s: any) => s.site_id))];
    let postOrders: any[] = [];
    if (siteIds.length) {
      const { data: po } = await supabase
        .from("post_orders")
        .select("id, site_id, title, body, checklist, updated_at")
        .in("site_id", siteIds)
        .eq("active", true);
      postOrders = po ?? [];
    }
    const teammateIds = [...new Set((teammates ?? []).map((t: any) => t.user_id))];
    let profiles: any[] = [];
    if (teammateIds.length) {
      const { data: p } = await fetchByIds<any>("profiles", "id, full_name, email", teammateIds, "id", supabase).then((d) => ({ data: d, error: null }));
      profiles = p ?? [];
    }

    return {
      memberships: memberships ?? [],
      partnerships: partnerships ?? [],
      sites: (siteRows ?? []).map((s: any) => ({
        partnership_id: s.partnership_id,
        ...(s.sites ?? {}),
        id: s.sites?.id ?? s.site_id,
      })),
      postOrders,
      shifts: shifts ?? [],
      payPeriods: periods ?? [],
      teammates: (teammates ?? []).map((t: any) => ({
        ...t,
        profile: profiles.find((p) => p.id === t.user_id) ?? null,
      })),
    };
  });

export const claimContractorShift = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ shiftId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: shift, error } = await context.supabase.rpc("claim_contractor_shift", {
      _shift_id: data.shiftId,
    } as any);
    if (error) throw new Response(error.message, { status: 400 });
    return { shift };
  });

export const requestContractorReplacement = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ shiftId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    const { data: shift, error } = await context.supabase.rpc("release_contractor_shift", {
      _shift_id: data.shiftId,
    } as any);
    if (error) throw new Response(error.message, { status: 400 });
    return { shift };
  });

// ---------------------------------------------------------------------------
// Pay periods / timesheets
// ---------------------------------------------------------------------------

export const buildContractorPayPeriod = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ partnershipId: uuid, periodStart: isoDate, periodEnd: isoDate }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { data: id, error } = await context.supabase.rpc("build_contractor_pay_period", {
      _partnership: data.partnershipId,
      _start: data.periodStart,
      _end: data.periodEnd,
    } as any);
    if (error) throw new Response(error.message, { status: 400 });
    return { payPeriodId: id };
  });

export const getContractorPayPeriod = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ payPeriodId: uuid }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: period, error } = await supabase
      .from("contractor_pay_periods")
      .select("*")
      .eq("id", data.payPeriodId)
      .maybeSingle();
    if (error) throw new Response(error.message, { status: 400 });
    if (!period) throw new Response("Not found", { status: 404 });
    const { data: items } = await supabase
      .from("contractor_pay_period_items")
      .select("*, sites(name)")
      .eq("pay_period_id", data.payPeriodId)
      .order("shift_starts_at", { ascending: true });
    const userIds = [...new Set((items ?? []).map((i: any) => i.member_user_id).filter(Boolean))];
    let profiles: any[] = [];
    if (userIds.length) {
      const { data: p } = await fetchByIds<any>("profiles", "id, full_name, email", userIds, "id", supabase).then((d) => ({ data: d, error: null }));
      profiles = p ?? [];
    }
    const { data: partnership } = await supabase
      .from("contractor_partnerships")
      .select("id, contractor_name, inviting_org_name, inviting_org_id")
      .eq("id", period.partnership_id)
      .maybeSingle();
    return {
      period,
      partnership,
      items: (items ?? []).map((i: any) => ({
        ...i,
        siteName: i.sites?.name ?? null,
        memberName:
          profiles.find((p) => p.id === i.member_user_id)?.full_name ??
          profiles.find((p) => p.id === i.member_user_id)?.email ??
          "Contractor member",
      })),
    };
  });

export const setPayPeriodStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      payPeriodId: uuid,
      status: z.enum(["ready_for_review", "verified", "submitted", "under_review", "approved", "returned"]),
      notes: z.string().max(1000).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: Record<string, unknown> = { status: data.status };
    if (data.notes !== undefined) patch["notes"] = data.notes;
    const now = new Date().toISOString();
    if (data.status === "verified") { patch["verified_by"] = userId; patch["verified_at"] = now; }
    if (data.status === "submitted") patch["submitted_at"] = now;
    if (data.status === "under_review") patch["received_at"] = now;
    if (data.status === "approved" || data.status === "returned") patch["decided_at"] = now;

    const { data: row, error } = await supabase
      .from("contractor_pay_periods")
      .update(patch as any)
      .eq("id", data.payPeriodId)
      .select("*")
      .single();
    if (error || !row) throw new Response(error?.message ?? "Could not update", { status: 400 });

    await supabase.from("audit_log").insert({
      organization_id: row.inviting_org_id,
      actor_id: userId,
      action: `contractor.pay_period_${data.status}`,
      target_type: "contractor_pay_period",
      target_id: row.id,
      metadata: { period_start: row.period_start, period_end: row.period_end, total_hours: row.total_hours },
    } as any);

    // Notify the other side of the relationship.
    const { data: members } = await supabase
      .from("contractor_members")
      .select("user_id, contractor_role")
      .eq("partnership_id", row.partnership_id)
      .eq("active", true);
    const contractorOwners = (members ?? [])
      .filter((m: any) => m.contractor_role === "contractor_owner")
      .map((m: any) => m.user_id);
    let recipients: string[] = contractorOwners;
    if (data.status === "submitted") {
      const managers = await fetchOrgMembersWith<{ user_id: string }>(
        supabase, row.inviting_org_id, "user_id",
        (q) => q.in("role", ["org_owner", "admin", "supervisor"]),
      );
      recipients = managers.map((m) => m.user_id);
    }
    if (recipients.length) {
      await supabase.rpc("fanout_notification", {
        _org: row.inviting_org_id,
        _recipients: recipients,
        _actor: userId,
        _source: "contractor",
        _source_id: row.id,
        _event_type: `contractor_pay_period_${data.status}`,
        _severity: data.status === "returned" ? "warning" : "info",
        _title: "Contractor timesheet update",
        _body: `Pay period ${row.period_start} — ${row.period_end} is now ${data.status.replace(/_/g, " ")}.`,
        _link: data.status === "submitted" ? `/contractors/${row.partnership_id}` : "/contractor/pay-periods",
        _metadata: {},
      } as any);
    }
    return { period: row };
  });
